import { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaDownload, FaPlay, FaPlus, FaUser, FaCopy, FaEdit, FaTrash, FaShoppingCart, FaRedo } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { AUDIENCE_QUERY } from '../queries';
import { RETRY_AUDIENCE_CRAWL } from '../mutations';
import { incrementName } from '../utils';
import { isStripePaymentRequired } from '../utils/featureFlags';

// An audience row is in exactly one of three states, not two. Collapsing the
// last two into "not crawled" is what showed users a wait-and-see message for
// an audience whose preparation had permanently given up.
const PROCESSING_MESSAGE =
  'Audience data is still being processed. Please wait until it completes.';
const FAILED_MESSAGE = "Preparing this audience didn't complete. Retry?";
const FAILED_ELSEWHERE_MESSAGE =
  "Preparing this audience didn't complete. Use Retry preparation first.";

interface ActionsDropdownProps {
  row: any;
  orderType?: 'audience' | 'enhancement' | 'insight' | 'file';
  audienceName?: string;
  showEnhance?: boolean;
  showProfile?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showBuy?: boolean;
  showDownload?: boolean;
  showActivate?: boolean;
  showDuplicate?: boolean;
  editLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onBuy?: () => void;
  onEnhance?: () => void;
  onProfile?: () => void;
}

export default function ActionsDropdown({
  row,
  orderType,
  audienceName,
  showEnhance = false,
  showProfile = false,
  showEdit = false,
  showDelete = false,
  showBuy = false,
  showDownload = true,
  showActivate = true,
  showDuplicate = true,
  editLabel = 'Edit',
  onEdit,
  onDelete,
  onBuy,
  onEnhance,
  onProfile,
}: ActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ horizontal: 'left' | 'right', vertical: 'bottom' | 'top' }>({ horizontal: 'right', vertical: 'bottom' });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const { loading, error, data } = useQuery(AUDIENCE_QUERY, {
    variables: { audienceName },
    fetchPolicy: 'cache-and-network',
    pollInterval: 10000,
    skip: !audienceName,
  });

  const audienceId = data?.audience?.id;
  const filters = data?.audience?.filters;
  const crawled = data?.audience?.crawledAt != null;
  // Terminal until someone retries: nothing ever produced data for the Glue
  // crawler to find, so waiting longer changes nothing.
  const crawlFailed = !crawled && data?.audience?.crawlingFailed === true;

  const [retryCrawl, { loading: retrying, error: retryError }] = useMutation(
    RETRY_AUDIENCE_CRAWL
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 192; // w-48 = 12rem = 192px
      
      const isAudience = orderType === 'audience';
      
      // Calculate dynamic dropdown height based on visible items
      let itemCount = 0;
      if (showDownload && orderType) itemCount++;
      if (showActivate && isAudience) itemCount++;
      if (showEnhance) itemCount++;
      if (showProfile) itemCount++;
      if (showEdit) itemCount++;
      if (showDelete) itemCount++;
      if (showBuy) itemCount++;
      if (showDuplicate && isAudience) itemCount++;
      
      const dropdownHeight = Math.max(50, itemCount * 40 + 16); // 40px per item + padding
      const padding = 16; // Safety padding
      
      // Check horizontal positioning
      const spaceOnRight = viewportWidth - buttonRect.right;
      const spaceOnLeft = buttonRect.left;
      
      let horizontal: 'left' | 'right' = 'right';
      if (spaceOnRight < dropdownWidth + padding && spaceOnLeft > dropdownWidth + padding) {
        horizontal = 'left';
      }
      
      // Check vertical positioning
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      let vertical: 'bottom' | 'top' = 'bottom';
      if (spaceBelow < dropdownHeight + padding && spaceAbove > dropdownHeight + padding) {
        vertical = 'top';
      }
      
      setDropdownPosition({ horizontal, vertical });
    }
  }, [isOpen]);

  const handleDownload = () => {
    setIsOpen(false);
  };

  const handleActivate = () => {
    if (audienceId) {
      navigate(`../../activations/activationbuilder?audID=${audienceId}`);
    }
    setIsOpen(false);
  };

  const handleEnhance = () => {
    if (onEnhance) {
      onEnhance();
    }
    setIsOpen(false);
  };

  const handleProfile = () => {
    if (onProfile) {
      onProfile();
    }
    setIsOpen(false);
  };

  const handleRetryCrawl = async () => {
    if (!audienceId) return;
    try {
      await retryCrawl({ variables: { audienceId } });
    } catch {
      // Rendered from `retryError` below — the menu deliberately stays open so
      // the user sees either the failure or the switch back to "processing".
    }
  };

  const handleDuplicate = () => {
    if (filters && audienceName) {
      navigate('../../audiences/audiencebuilder', {
        state: {
          initialLoadState: {
            creatingNewAudience: true,
            newAudienceName: incrementName(audienceName),
            audienceId,
            audienceName,
            filters,
          },
        },
      });
    }
    setIsOpen(false);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
    setIsOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsOpen(false);
  };

  const handleBuy = () => {
    if (onBuy) {
      onBuy();
    }
    setIsOpen(false);
  };

  const isAudience = orderType === 'audience';
  const isFile = orderType === 'file';
  const canActivate = isAudience && !loading && !error && audienceId;
  const canDuplicate = isAudience && !loading && (!error || filters) && crawled;
  // Audience rows must finish crawling before they can be enhanced/profiled.
  // File rows (Uploaded Files) have no audience to crawl — the parent only
  // shows these actions once the file has finished processing, so enable them
  // whenever they are shown.
  const canEnhance = isFile ? showEnhance : canActivate && crawled;
  const canProfile = isFile ? showProfile : canActivate && crawled;
  // The failed state must NOT be inert — it is the entry point for the retry,
  // which re-runs the export and wakes the crawler.
  const canRetry = isAudience && crawlFailed && !!audienceId && !retrying;

  const audienceStateTitle = (() => {
    if (isFile || !isAudience || crawled) return undefined;
    return crawlFailed ? FAILED_ELSEWHERE_MESSAGE : PROCESSING_MESSAGE;
  })();

  // Map orderType to the correct route path for download links
  const getDownloadRoutePath = (type: string) => {
    switch (type) {
      case 'audience-enhancement':
      case 'enhancement':
        return 'enhancements';
      case 'insight':
        return 'insights';
      case 'audience':
        return 'audiences';
      case 'audience-profile':
        return 'insights/audience';
      default:
        return `${type}s`;
    }
  };

  const downloadRoutePath = orderType ? getDownloadRoutePath(orderType) : '';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[100px]"
      >
        <span>Actions</span>
        <FaChevronDown className={`ml-2 h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute ${
          dropdownPosition.horizontal === 'right' ? 'right-0' : 'left-0'
        } ${
          dropdownPosition.vertical === 'bottom' ? 'mt-1' : 'mb-1 bottom-full'
        } z-50 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto`}>
          <div className="py-1">
            {showDownload && orderType && (
              <Link
                to={`../../${downloadRoutePath}/order/${row.original.id}`}
                onClick={handleDownload}
                className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-150"
              >
                <FaDownload className="mr-3 h-4 w-4" />
                Download
              </Link>
            )}

            {showActivate && isAudience && (
              <button
                type="button"
                onClick={handleActivate}
                disabled={!canActivate}
                className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors duration-150 ${
                  canActivate
                    ? 'hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <FaPlay className="mr-3 h-4 w-4" />
                Activate
              </button>
            )}

            {showEnhance && (
              <button
                type="button"
                onClick={handleEnhance}
                disabled={!canEnhance}
                title={audienceStateTitle}
                className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors duration-150 ${
                  canEnhance
                    ? 'hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <FaPlus className="mr-3 h-4 w-4" />
                Enhance
              </button>
            )}

            {showProfile && (
              <button
                type="button"
                // In the failed state this row is the recovery affordance
                // rather than the profile flow — the label says so.
                onClick={canRetry ? handleRetryCrawl : handleProfile}
                disabled={!canProfile && !canRetry}
                title={crawlFailed ? FAILED_MESSAGE : audienceStateTitle}
                className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors duration-150 ${
                  canProfile || canRetry
                    ? 'hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                {crawlFailed ? (
                  <FaRedo className="mr-3 h-4 w-4" />
                ) : (
                  <FaUser className="mr-3 h-4 w-4" />
                )}
                {retrying
                  ? 'Retrying…'
                  : crawlFailed
                    ? 'Retry preparation'
                    : 'Profile'}
              </button>
            )}

            {crawlFailed && retryError && (
              <div className="px-4 py-2 text-xs text-red-600">
                {retryError.message}
              </div>
            )}

            {showEdit && (
              <button
                type="button"
                onClick={handleEdit}
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors duration-150"
              >
                <FaEdit className="mr-3 h-4 w-4" />
                {editLabel}
              </button>
            )}

            {showDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 transition-colors duration-150"
              >
                <FaTrash className="mr-3 h-4 w-4" />
                Delete
              </button>
            )}

            {showBuy && (
              <button
                type="button"
                onClick={handleBuy}
                className="flex items-center w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-blue-50 transition-colors duration-150"
              >
                <FaShoppingCart className="mr-3 h-4 w-4" />
                {isStripePaymentRequired() ? 'Buy' : 'Order'}
              </button>
            )}

            {showDuplicate && isAudience && (
              <button
                type="button"
                onClick={handleDuplicate}
                disabled={!canDuplicate}
                className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors duration-150 ${
                  canDuplicate
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <FaCopy className="mr-3 h-4 w-4" />
                Duplicate
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}